import { moveCard } from "@lourenci/react-kanban";
import { ThunkDispatch, UnknownAction } from "@reduxjs/toolkit";
import { RootState } from "@reduxjs/toolkit/query";
import { IIssueBoardApi } from "@schemas/issueBoardSchema";
import { IIssueApi } from "@schemas/issueSchema";
import {
  GetProjectByProjectIdConnectedTasksApiArg,
  GetProjectByProjectIdIssuesOnBoardApiArg,
  openapi,
  PostProjectByProjectIdIssuesOnBoardsApiArg,
  PostProjectByProjectIdIssuesOnBoardsApiResponse,
  PutProjectByProjectIdIssuesKanbanreorderApiResponse,
} from "../../../../openapi";
import { apiSlice } from "../../../apiSlice";
import {
  setNotificationInRedux,
  setShowFailedMessageThunk,
  setShowSuccessMessageThunk,
} from "../../../global/globalSlice";
import { ToastMessagesEnums } from "../../../mc-shared/sharedConstants";
import { classifyTidsfrist, computeDueForTidsfristBucket } from "../../../mc-shared/sharedUtils";
import { trpcSlice } from "../../../trpcSlice";
import { issueBoardRTK } from "./IssueBoardRTK";
import { multiRegisterIssuesApi } from "./MultiRegisterIssues/multiRegisterIssuesApi";
import { selectHttpIssuesOnBoard, selectKanbanBoardColumns } from "./issueSelectors";
import { addMissingKeysIssue } from "./issueUtils";
import { IKanbanBoard, IKanbanFrom, IKanbanTo } from "./types";

const SET_ACTIVE_TEMPLATE_ACTION = "issue/setIssueActiveTableTemplateIdInRedux";

const issueBoardHasAutomaticFormula = (issueBoard?: IIssueBoardApi | null) =>
  Object.values(issueBoard?.otherAttributesConfig ?? {}).some(
    (attribute) =>
      attribute.typeOfValue === "FORMULA" && attribute.isActive === true && attribute.formula?.counterMode !== "MANUAL",
  );

const shouldInvalidateIssueFormulasAfterPropertyPatch = (
  projectId: string,
  issue: IIssueApi,
  property: Record<string, unknown>,
  state: RootState<any, any, "trpc">,
) => {
  if (property.otherAttributesData == null) {
    return false;
  }

  const issueBoardId = issue.issueBoard?._id;
  if (issueBoardId == null) {
    return false;
  }

  const issueBoard = issueBoardRTK.endpoints.getSingleIssueBoard.select({
    projectId,
    issueBoardId,
  })(state as any).data;

  return issueBoardHasAutomaticFormula(issueBoard);
};

function getDoGetAllIssuesArgs(
  stateOrGetState: unknown | (() => unknown),
  api: { reducerPath: string },
): GetProjectByProjectIdIssuesOnBoardApiArg[] {
  const state = typeof stateOrGetState === "function" ? (stateOrGetState as () => unknown)() : stateOrGetState;
  const queries = (state as any)?.[api.reducerPath]?.queries ?? {};
  const out: GetProjectByProjectIdIssuesOnBoardApiArg[] = [];

  for (const key of Object.keys(queries)) {
    if (!key.startsWith("getProjectByProjectIdIssuesOnBoard(")) continue;
    const open = key.indexOf("(");
    if (open === -1 || !key.endsWith(")")) continue;

    try {
      const json = key.slice(open + 1, -1);
      const args = JSON.parse(json) as GetProjectByProjectIdIssuesOnBoardApiArg;
      out.push(args);
    } catch {
      // ignore parse error
    }
  }

  return out;
}

function getDoGetAllIssuesArgsForBoard(
  stateOrGetState: unknown | (() => unknown),
  api: { reducerPath: string },
  _projectId: string,
  _issueBoardId: string,
): GetProjectByProjectIdIssuesOnBoardApiArg[] {
  return getDoGetAllIssuesArgs(stateOrGetState, api);
}

function getMultiRegisterIssuesArgs(
  stateOrGetState: unknown | (() => unknown),
  api: { reducerPath: string },
): PostProjectByProjectIdIssuesOnBoardsApiArg[] {
  const state = typeof stateOrGetState === "function" ? (stateOrGetState as () => unknown)() : stateOrGetState;
  const queries = (state as any)?.[api.reducerPath]?.queries ?? {};
  const out: PostProjectByProjectIdIssuesOnBoardsApiArg[] = [];

  for (const key of Object.keys(queries)) {
    if (!key.startsWith("getMultiRegisterIssuesOnBoards(")) continue;
    const open = key.indexOf("(");
    if (open === -1 || !key.endsWith(")")) continue;

    try {
      const json = key.slice(open + 1, -1);
      out.push(JSON.parse(json) as PostProjectByProjectIdIssuesOnBoardsApiArg);
    } catch {
      // ignore parse error
    }
  }

  return out;
}

function getConnectedTasksArgs(
  stateOrGetState: unknown | (() => unknown),
  api: { reducerPath: string },
): GetProjectByProjectIdConnectedTasksApiArg[] {
  const state = typeof stateOrGetState === "function" ? (stateOrGetState as () => unknown)() : stateOrGetState;
  const queries = (state as any)?.[api.reducerPath]?.queries ?? {};
  const out: GetProjectByProjectIdConnectedTasksApiArg[] = [];

  for (const key of Object.keys(queries)) {
    if (!key.startsWith("getProjectByProjectIdConnectedTasks(")) continue;
    const open = key.indexOf("(");
    if (open === -1 || !key.endsWith(")")) continue;

    try {
      const json = key.slice(open + 1, -1);
      out.push(JSON.parse(json) as GetProjectByProjectIdConnectedTasksApiArg);
    } catch {
      // ignore parse error
    }
  }

  return out;
}

const issueMatchesMultiRegisterQuery = (issue: IIssueApi, args: PostProjectByProjectIdIssuesOnBoardsApiArg) => {
  const issueBoardId = String(issue.issueBoard?._id ?? "");
  const filter = args.body.filter;

  if (!args.body.issueboardIds?.some((boardId) => String(boardId) === issueBoardId)) return false;
  if (filter?.issueboardIds != null && !filter.issueboardIds.some((boardId) => String(boardId) === issueBoardId)) {
    return false;
  }
  if (filter?.taskOrIssue != null && issue.taskOrIssue !== filter.taskOrIssue) return false;
  if (filter?.status != null && filter.status.length > 0 && !filter.status.includes(issue.status)) return false;
  if (
    filter?.assignedToIds != null &&
    filter.assignedToIds.length > 0 &&
    !filter.assignedToIds.includes(String(issue.assignedTo?._id ?? ""))
  ) {
    return false;
  }

  return true;
};

const decrementBoardCount = (
  boardCounts: PostProjectByProjectIdIssuesOnBoardsApiResponse["boardCounts"],
  issueBoardId: string,
) => {
  const count = boardCounts.find((item) => String(item.issueboardId) === issueBoardId);
  if (count != null) count.count = Math.max(count.count - 1, 0);
};

const incrementBoardCount = (
  boardCounts: PostProjectByProjectIdIssuesOnBoardsApiResponse["boardCounts"],
  issueBoardId: string,
) => {
  const count = boardCounts.find((item) => String(item.issueboardId) === issueBoardId);
  if (count != null) {
    count.count += 1;
  } else {
    boardCounts.push({ issueboardId: issueBoardId, count: 1 });
  }
};

const isPlainRecord = (value: unknown): value is Record<string, unknown> =>
  value != null && typeof value === "object" && Object.getPrototypeOf(value) === Object.prototype;

const mergeIssuePropertyPatch = (target: Record<string, any>, patch: Record<string, any>) => {
  for (const [key, value] of Object.entries(patch)) {
    if (isPlainRecord(value) && isPlainRecord(target[key])) {
      mergeIssuePropertyPatch(target[key], value);
    } else {
      target[key] = value;
    }
  }
};

const patchIssueOptimistically = (
  projectId: string,
  issueId: string,
  property: Record<string, any>,
  getState: () => RootState<any, any, "trpc">,
  dispatch: ThunkDispatch<any, any, UnknownAction>,
) => {
  const sameId = (a: unknown, b: unknown) => String(a) === String(b);
  const patches: { undo?: () => void }[] = [];
  const patchIssue = (issue: IIssueApi) => mergeIssuePropertyPatch(issue as unknown as Record<string, any>, property);

  patches.push(
    dispatch(
      openapi.util.updateQueryData(
        "getProjectByProjectIdIssuesGetSingle",
        { projectId, issueId },
        (draft: IIssueApi) => {
          if (draft && sameId(draft._id, issueId)) {
            patchIssue(draft);
          }
        },
      ),
    ),
  );

  for (const args of getDoGetAllIssuesArgs(getState, trpcSlice)) {
    patches.push(
      dispatch(
        openapi.util.updateQueryData("getProjectByProjectIdIssuesOnBoard", args, (draft: IIssueApi[]) => {
          const issue = draft.find((item) => sameId(item._id, issueId));
          if (issue != null) {
            patchIssue(issue);
          }
        }),
      ),
    );
  }

  for (const args of getMultiRegisterIssuesArgs(getState, trpcSlice)) {
    patches.push(
      dispatch(
        multiRegisterIssuesApi.util.updateQueryData("getMultiRegisterIssuesOnBoards", args, (draft) => {
          const idx = draft.items.findIndex((item) => sameId(item._id, issueId));
          if (idx === -1) return;

          const oldIssue = draft.items[idx];
          patchIssue(oldIssue as unknown as IIssueApi);

          if (issueMatchesMultiRegisterQuery(oldIssue as unknown as IIssueApi, args) === false) {
            draft.items.splice(idx, 1);
            decrementBoardCount(draft.boardCounts, String(oldIssue.issueBoard?._id ?? ""));
            if (draft.totalCount != null) draft.totalCount = Math.max(draft.totalCount - 1, 0);
          }
        }),
      ),
    );
  }

  return patches;
};

export const patchMultiRegisterIssueCaches = (
  issue: IIssueApi,
  getState: () => RootState<any, any, "trpc">,
  dispatch: ThunkDispatch<any, any, UnknownAction>,
) => {
  if (!issue?._id) return;

  const sameId = (a: unknown, b: unknown) => String(a) === String(b);
  const argsList = getMultiRegisterIssuesArgs(getState, trpcSlice);

  for (const args of argsList) {
    dispatch(
      multiRegisterIssuesApi.util.updateQueryData("getMultiRegisterIssuesOnBoards", args, (draft) => {
        const idx = draft.items.findIndex((item) => sameId(item._id, issue._id));
        const shouldKeepInResult = issueMatchesMultiRegisterQuery(issue, args);

        if (idx === -1) return;

        const oldIssue = draft.items[idx];
        const oldBoardId = String(oldIssue.issueBoard?._id ?? "");
        const newBoardId = String(issue.issueBoard?._id ?? "");

        if (shouldKeepInResult) {
          draft.items[idx] = issue as any;
          if (oldBoardId !== newBoardId) {
            decrementBoardCount(draft.boardCounts, oldBoardId);
            incrementBoardCount(draft.boardCounts, newBoardId);
          }
        } else {
          draft.items.splice(idx, 1);
          decrementBoardCount(draft.boardCounts, oldBoardId);
          if (draft.totalCount != null) draft.totalCount = Math.max(draft.totalCount - 1, 0);
        }
      }),
    );
  }
};

openapi.enhanceEndpoints({
  addTagTypes: ["FormulaGroupIssues"],
  endpoints: {
    getProjectByProjectIdIssuesTrpcIssueGetConnectedIssues: {
      providesTags: ["ConnectedIssues"],
    },
    getProjectByProjectIdIssuesGetSingle: {
      providesTags: ["ConnectedIssues", "Issue"],
    },
    getProjectByProjectIdIssueFormulaGroupIssues: {
      providesTags: ["FormulaGroupIssues"],
    },
    putProjectByProjectIdIssuesKanbanreorder: {
      invalidatesTags: ["Issue", "ConnectedIssues"],
      async onQueryStarted({ projectId, body }, { dispatch, getState, queryFulfilled }) {
        const { issueBoardId, issueId, attributeChanged, targetColumn, targetPosition } = body;

        const issuesOnBoard = selectHttpIssuesOnBoard(getState() as any);
        const issue = issuesOnBoard.find((i) => i._id === issueId);
        const sameId = (a: unknown, b: unknown) => String(a) === String(b);
        const kanbanBoardColumns = selectKanbanBoardColumns(getState() as any);
        const colIndexByValue = (value: string) => kanbanBoardColumns.findIndex((c) => c.value === value);

        let toColumnId = -1;
        if (attributeChanged === "bucket") {
          toColumnId = colIndexByValue(targetColumn);
        } else if (attributeChanged === "status") {
          toColumnId = colIndexByValue(targetColumn);
        } else {
          toColumnId = colIndexByValue(targetColumn);
        }

        let fromColumnId = -1;
        let fromPosition = 0;

        if (attributeChanged === "bucket") {
          const fromValue = issue?.bucket ?? "";
          fromColumnId = colIndexByValue(fromValue);
        } else if (attributeChanged === "status") {
          const fromValue = issue?.status ?? "";
          fromColumnId = colIndexByValue(fromValue);
        } else {
          const fromValue = classifyTidsfrist(issue?.dates?.due ?? null);
          fromColumnId = colIndexByValue(fromValue);
        }

        if (fromColumnId >= 0) {
          const col = kanbanBoardColumns[fromColumnId];
          const cards = col?.cards ?? [];
          const idxInCol = cards.findIndex((c: any) => sameId(String(c?.content?._id ?? c?._id), issueId));
          fromPosition = idxInCol >= 0 ? idxInCol : issue?.kanbanOrder?.[attributeChanged] ?? 0;
        }

        const kanbanTo: IKanbanTo = { toColumnId, toPosition: targetPosition };
        const kanbanFrom: IKanbanFrom = { fromColumnId, fromPosition };
        const updatedBoard: IKanbanBoard<IIssueApi> = moveCard({ columns: kanbanBoardColumns }, kanbanFrom, kanbanTo);

        const posById = new Map<string, { column: string; index: number }>();
        for (const col of updatedBoard.columns) {
          for (let i = 0; i < col.cards.length; i++) {
            const c = col.cards[i] as any;
            const id: string = String(c?.content?._id ?? c?._id);
            posById.set(id, { column: col.value as string, index: i });
          }
        }

        const argsList = getDoGetAllIssuesArgsForBoard(getState, trpcSlice, projectId, issueBoardId);
        const undos = argsList.map((args) =>
          dispatch(
            openapi.util.updateQueryData("getProjectByProjectIdIssuesOnBoard", args, (draft: IIssueApi[]) => {
              const attr = attributeChanged as "status" | "bucket" | "tidsfrist";

              for (let i = 0; i < draft.length; i++) {
                const it = draft[i];
                const pos = posById.get(String(it._id));
                if (!pos) continue;

                const nextKo = { ...(it.kanbanOrder ?? {}), [attr]: pos.index };
                let nextItem: IIssueApi = it;
                if (attr !== "tidsfrist") {
                  nextItem = { ...nextItem, [attr]: pos.column } as IIssueApi;
                } else if (sameId(it._id, issueId)) {
                  nextItem = {
                    ...nextItem,
                    dates: {
                      ...(nextItem.dates ?? {}),
                      due: computeDueForTidsfristBucket(pos.column as any, nextItem?.dates?.due ?? null) as any,
                    },
                  } as IIssueApi;
                }

                if ((it.kanbanOrder as any)?.[attr] !== pos.index) {
                  nextItem = { ...nextItem, kanbanOrder: nextKo } as IIssueApi;
                }

                if (nextItem !== it) draft[i] = nextItem;
              }

              draft.sort((a, b) => ((a.kanbanOrder as any)?.[attr] ?? 0) - ((b.kanbanOrder as any)?.[attr] ?? 0));
            }),
          ),
        );

        try {
          const { data } = (await queryFulfilled) as { data: PutProjectByProjectIdIssuesKanbanreorderApiResponse };

          for (const args of argsList) {
            dispatch(
              openapi.util.updateQueryData("getProjectByProjectIdIssuesOnBoard", args, (draft: IIssueApi[]) => {
                const attr = body.attributeChanged as "status" | "bucket" | "tidsfrist";
                const idx = draft.findIndex((i) => sameId(i._id, (data as any).updatedIssue._id));
                if (idx >= 0) {
                  draft[idx] = data.updatedIssue as IIssueApi;
                }
                for (const ch of data.changed) {
                  const changedIdx = draft.findIndex((i) => String(i._id) === String(ch.id));
                  if (changedIdx >= 0) {
                    const it = draft[changedIdx];
                    draft[changedIdx] = {
                      ...it,
                      kanbanOrder: {
                        ...(it.kanbanOrder ?? {}),
                        [attributeChanged]: ch.index,
                      },
                    };
                  }
                }
                if (attr !== "tidsfrist") {
                  const movedIdx = draft.findIndex((i) => String(i._id) === String(data.moved.id));
                  if (movedIdx >= 0) {
                    const it = draft[movedIdx];
                    draft[movedIdx] = { ...it, [attr]: data.moved.column };
                  }
                }
                draft.sort((a, b) => ((a.kanbanOrder as any)?.[attr] ?? 0) - ((b.kanbanOrder as any)?.[attr] ?? 0));
              }),
            );
          }
          dispatch(openapi.util.invalidateTags(["Issue", "ConnectedIssues", "NotificationIssueStatuses"]));
          dispatch(openapi.util.invalidateTags(["TrpcMembersByUserRelation", "Whoami"]));
        } catch {
          dispatch(setShowFailedMessageThunk("Kunne ikke flytte sak"));
          undos.forEach((u) => u.undo?.());
        }
      },
    },
    putProjectByProjectIdIssueUpdateUser: {
      async onQueryStarted({ projectId }, { dispatch, getState, queryFulfilled }) {
        const { data: issue } = await queryFulfilled;
        updateIssueAfterResp(projectId, issue, getState, dispatch);
      },
    },
    putProjectByProjectIdIssueUpdateSimple: {
      async onQueryStarted({ projectId, body }, { dispatch, getState, queryFulfilled }) {
        const undoPatches = patchIssueOptimistically(
          projectId,
          body._id,
          body.property as Record<string, any>,
          getState,
          dispatch,
        );

        try {
          const { data: issue } = await queryFulfilled;
          updateIssueAfterResp(projectId, issue, getState, dispatch);
          if (body.property.tags != null) {
            dispatch(apiSlice.util.invalidateTags(["Tags"]));
          }
          if (
            shouldInvalidateIssueFormulasAfterPropertyPatch(
              projectId,
              issue,
              body.property as Record<string, unknown>,
              getState(),
            )
          ) {
            dispatch(apiSlice.util.invalidateTags(["IssueFormulas"]));
          }
        } catch {
          undoPatches.forEach((patch) => patch.undo?.());
          dispatch(setShowFailedMessageThunk("Kunne ikke lagre endringer"));
        }
      },
    },
    putProjectByProjectIdIssueUpdateFormulaCounter: {
      async onQueryStarted({ projectId }, { dispatch, getState, queryFulfilled }) {
        try {
          const { data: issue } = await queryFulfilled;
          updateIssueAfterResp(projectId, issue, getState, dispatch);
        } catch {
          // Input-komponenten visar ett fältnära felmeddelande. Här sväljer vi lifecycle-felet
          // så RTK Query inte skickar det vidare till appens globala error boundary.
        }
      },
    },
    putProjectByProjectIdIssueConnectedBimSpaces: {
      async onQueryStarted({ projectId }, { dispatch, getState, queryFulfilled }) {
        const { data: issue } = await queryFulfilled;
        updateIssueAfterResp(projectId, issue, getState, dispatch);
      },
    },
    putProjectByProjectIdIssueItemAndIssueIdCommentCommentIdReaction: {
      async onQueryStarted({ projectId }, { dispatch, getState, queryFulfilled }) {
        const { data: issue } = await queryFulfilled;
        updateIssueAfterResp(projectId, issue, getState, dispatch);
      },
    },
    postProjectByProjectIdIssueTodosAdd: {
      async onQueryStarted({ projectId }, { dispatch, getState, queryFulfilled }) {
        const { data: issue } = await queryFulfilled;
        updateIssueAfterResp(projectId, issue, getState, dispatch);
      },
    },
    putProjectByProjectIdIssueTodosUpdate: {
      async onQueryStarted({ projectId }, { dispatch, getState, queryFulfilled }) {
        const { data: issue } = await queryFulfilled;
        updateIssueAfterResp(projectId, issue, getState, dispatch);
      },
    },
    putProjectByProjectIdIssueTodosToggle: {
      async onQueryStarted({ projectId }, { dispatch, getState, queryFulfilled }) {
        const { data: issue } = await queryFulfilled;
        updateIssueAfterResp(projectId, issue, getState, dispatch);
      },
    },
    deleteProjectByProjectIdIssueTodosDelete: {
      async onQueryStarted({ projectId }, { dispatch, getState, queryFulfilled }) {
        const { data: issue } = await queryFulfilled;
        updateIssueAfterResp(projectId, issue, getState, dispatch);
      },
    },
    putProjectByProjectIdIssueConnectedTasksUpdateOrder: {
      invalidatesTags: ["ConnectedTasks", "Issues"],
      async onQueryStarted({ projectId }, { dispatch, getState, queryFulfilled }) {
        const { data: issue } = await queryFulfilled;
        updateIssueAfterResp(projectId, issue, getState, dispatch);
      },
    },
    putProjectByProjectIdIssueSubscribe: {
      async onQueryStarted({ projectId }, { dispatch, getState, queryFulfilled }) {
        const { data: issue } = await queryFulfilled;
        updateIssueAfterResp(projectId, issue, getState, dispatch);
      },
    },
    getProjectByProjectIdIssuesOnBoard: {
      providesTags: ["Issues"],
    },
    postProjectByProjectIdIssuesCreate: {
      async onQueryStarted({ projectId, body }, { dispatch, queryFulfilled, getState }) {
        try {
          const resp = await queryFulfilled;
          const createdIssue = addMissingKeysIssue(resp.data as any, "");
          const argsList = getDoGetAllIssuesArgsForBoard(getState, trpcSlice, projectId, body.issueBoardId);

          for (const args of argsList) {
            dispatch(
              openapi.util.updateQueryData("getProjectByProjectIdIssuesOnBoard", args, (draft) => {
                if (!draft.some((issue) => issue._id === createdIssue._id)) {
                  draft.push(createdIssue as any);
                }
              }),
            );
          }

          dispatch(setShowSuccessMessageThunk(ToastMessagesEnums.ISSUE_ADDED));
          dispatch(openapi.util.invalidateTags(["ConnectedTasks"]));
          dispatch(openapi.util.invalidateTags(["TrpcMembersByUserRelation", "Whoami"]));
        } catch (err) {
          console.warn("postProjectByProjectIdIssuesCreate.onQueryStarted handled error:", err);
        }
      },
    },
    putProjectByProjectIdIssueArchive: {
      async onQueryStarted(_, { dispatch, queryFulfilled, getState }) {
        const { data: issue } = await queryFulfilled;
        const resp = await queryFulfilled;
        const taskOrIssue = resp.data.taskOrIssue;
        const projectId = resp.data.project;
        const issueBoardId = resp.data.issueBoard._id;
        const argsList = getDoGetAllIssuesArgsForBoard(getState, trpcSlice, projectId, issueBoardId);

        for (const args of argsList) {
          dispatch(
            openapi.util.updateQueryData("getProjectByProjectIdIssuesOnBoard", args, (draft) => {
              const index = draft.findIndex((i) => i._id === issue._id);
              if (index >= 0) draft.splice(index, 1);
            }),
          );

          if (taskOrIssue === "TASK") {
            dispatch(
              openapi.util.updateQueryData("getProjectByProjectIdIssuesOnBoard", args, (draft) => {
                draft.map((draftIssue) => {
                  if (draftIssue._id === resp.data.connectedIssue?._id) {
                    const idx = (draftIssue.connectedTasks as IIssueApi[]).findIndex((t) => t._id === issue._id);
                    if (idx >= 0) (draftIssue.connectedTasks as IIssueApi[]).splice(idx, 1);
                  }
                  return draftIssue;
                });
              }),
            );
          }
        }

        dispatch(openapi.util.invalidateTags(["ConnectedTasks"]));
        dispatch(openapi.util.invalidateTags(["TrpcMembersByUserRelation", "Whoami"]));
      },
      invalidatesTags: ["Issue"],
    },
    putProjectByProjectIdIssueConvertType: {
      invalidatesTags: ["Issue", "Issues", "ConnectedTasks", "IssueLogs"],
      async onQueryStarted({ projectId }, { dispatch, getState, queryFulfilled }) {
        try {
          const { data: issue } = await queryFulfilled;
          updateIssueAfterResp(projectId, issue, getState, dispatch);
        } catch (err) {
          dispatch(setShowFailedMessageThunk("Kunne ikke endre type"));
          console.warn("putProjectByProjectIdIssueConvertType failed:", err);
        }
      },
    },
    putIssueConnect: {
      invalidatesTags: ["ConnectedIssues"],
      async onQueryStarted({ body }, { dispatch, getState, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          const { projectId, issueBoardId } = body;
          const { source, target } = data;
          const argsList = getDoGetAllIssuesArgsForBoard(getState, trpcSlice, projectId, issueBoardId);
          for (const args of argsList) {
            dispatch(
              openapi.util.updateQueryData("getProjectByProjectIdIssuesOnBoard", args, (draft: IIssueApi[]) => {
                let hit = 0;
                for (let i = 0; i < draft.length; i++) {
                  const id = draft[i]._id;
                  if (id === source._id) {
                    draft[i] = source;
                    if (++hit === 2) break;
                  } else if (id === target._id) {
                    draft[i] = target;
                    if (++hit === 2) break;
                  }
                }
              }),
            );
          }
          dispatch(setShowSuccessMessageThunk("Kobling oppdatert"));
        } catch (err) {
          dispatch(
            setShowFailedMessageThunk(
              body.action === "DISCONNECT" ? "Kunne ikke fjerne kobling" : "Kunne ikke opprette kobling",
            ),
          );
          console.warn("putIssueConnect failed:", err);
        }
      },
    },
    postProjectByProjectIdIssuesCopy: {
      async onQueryStarted(copyArgs, { dispatch, queryFulfilled, getState }) {
        try {
          await queryFulfilled;
          const resp = await queryFulfilled;
          const createdIssue = resp.data;
          const args = getDoGetAllIssuesArgsForBoard(
            getState,
            trpcSlice,
            copyArgs.projectId,
            createdIssue.issueBoard._id,
          );

          for (const arg of args) {
            dispatch(
              openapi.util.updateQueryData("getProjectByProjectIdIssuesOnBoard", arg, (draft: IIssueApi[]) => {
                draft.push(createdIssue);
              }),
            );
          }
          dispatch(openapi.util.invalidateTags(["ConnectedTasks"]));
          dispatch(openapi.util.invalidateTags(["TrpcMembersByUserRelation", "Whoami"]));
        } catch (err) {
          dispatch(setShowFailedMessageThunk("Kunne ikke kopiere sak"));
          console.error("Issue copy failed:", err);
        }
      },
    },
    getIssueByUser: {
      providesTags: ["TrpcMembersByUserRelation"],
    },
    putProjectByProjectIdIssueUpdateDates: {
      async onQueryStarted({ projectId, body }, { dispatch, getState, queryFulfilled }) {
        const { issueId, dates } = body;
        const sameId = (a: unknown, b: unknown) => String(a) === String(b);
        const issuesOnBoard = selectHttpIssuesOnBoard(getState() as any);
        const issue = issuesOnBoard.find((i) => sameId(i._id, issueId));

        if (!issue) return;

        const argsList = getDoGetAllIssuesArgsForBoard(getState, trpcSlice, projectId, issue.issueBoard._id);
        const undos = argsList.map((args) =>
          dispatch(
            openapi.util.updateQueryData("getProjectByProjectIdIssuesOnBoard", args, (draft: IIssueApi[]) => {
              const idx = draft.findIndex((i) => sameId(i._id, issueId));
              if (idx >= 0) {
                draft[idx] = {
                  ...draft[idx],
                  dates: {
                    ...draft[idx].dates,
                    plannedStart: dates.plannedStart,
                    due: dates.due,
                  },
                };
              }
            }),
          ),
        );

        try {
          const { data } = await queryFulfilled;

          for (const args of argsList) {
            dispatch(
              openapi.util.updateQueryData("getProjectByProjectIdIssuesOnBoard", args, (draft: IIssueApi[]) => {
                const idx = draft.findIndex((i) => sameId(i._id, data.updatedIssue._id));
                if (idx >= 0) {
                  draft[idx] = data.updatedIssue;
                }
              }),
            );
          }
          dispatch(setShowSuccessMessageThunk("Datoer oppdatert"));
          dispatch(openapi.util.invalidateTags(["TrpcMembersByUserRelation", "Whoami"]));
        } catch {
          dispatch(setShowFailedMessageThunk("Kunne ikke oppdatere datoer"));
          undos.forEach((u) => u.undo?.());
        }
      },
    },
    getProjectByProjectIdTableTemplates: {
      providesTags: ["IssueTableTemplates"],
    },
    patchProjectByProjectIdTableTemplate: {
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled; // wait for backend success
          dispatch(openapi.util.invalidateTags(["IssueTableTemplates"]));
          dispatch(apiSlice.util.invalidateTags(["SingleIssueBoard"]));
          dispatch(
            setShowSuccessMessageThunk(arg?.body?.tableTemplate?.archived === true ? "Mal arkivert" : "Mal lagret"),
          );
        } catch (err) {
          dispatch(
            setShowFailedMessageThunk(
              arg?.body?.tableTemplate?.archived === true ? "Kunne ikke arkivere mal" : "Kunne ikke lagre mal",
            ),
          );
          console.error("Table template save failed:", err);
        }
      },
    },
    postProjectByProjectIdTableTemplate: {
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const resp = await queryFulfilled;
          dispatch(openapi.util.invalidateTags(["IssueTableTemplates"]));
          dispatch(apiSlice.util.invalidateTags(["SingleIssueBoard"]));
          dispatch(setShowSuccessMessageThunk("Mal opprettet"));
          dispatch({ type: SET_ACTIVE_TEMPLATE_ACTION, payload: resp.data._id });
        } catch (err) {
          dispatch(setShowFailedMessageThunk("Kunne ikke opprette mal"));
          console.error("Table template create failed:", err);
        }
      },
    },
    getProjectByProjectIdConnectedTasks: {
      providesTags: ["ConnectedTasks"],
    },
    postIssueboardFavoriteToggle: {
      async onQueryStarted({ body }, { dispatch, queryFulfilled }) {
        const { projectId, issueboardId } = body;

        try {
          const { data } = await queryFulfilled;
          const { isFavorite } = data;

          dispatch(
            openapi.util.updateQueryData("getIssueboardFavorites", { projectId }, (draft) => {
              if (isFavorite) {
                if (!draft.some((f) => f.issueboard === issueboardId)) {
                  draft.push({ _id: "", user: "", issueboard: issueboardId, project: projectId });
                }
              } else {
                const idx = draft.findIndex((f) => f.issueboard === issueboardId);
                if (idx >= 0) draft.splice(idx, 1);
              }
            }),
          );
        } catch {
          dispatch(setShowFailedMessageThunk("Kunne ikke oppdatere favoritt"));
        }
      },
    },
    getProjectByProjectIdIssueAndIssueIdLogs: {
      providesTags: ["IssueLogs"],
    },
  },
});

export const updateIssueAfterResp = async (
  projectId: string,
  issue: IIssueApi,
  getState: () => RootState<any, any, "trpc">,
  dispatch: ThunkDispatch<any, any, UnknownAction>,
) => {
  const sameId = (a: unknown, b: unknown) => String(a) === String(b);
  const issueBoardId = issue?.issueBoard?._id;

  if (!issueBoardId || !issue?._id) {
    return;
  }

  const argsList = getDoGetAllIssuesArgsForBoard(getState, trpcSlice, projectId, issueBoardId);
  const argsListSingleIssue = { projectId, issueId: issue._id };

  dispatch(
    openapi.util.updateQueryData("getProjectByProjectIdIssuesGetSingle", argsListSingleIssue, (draft: IIssueApi) => {
      if (draft && sameId(draft._id, issue._id)) {
        Object.assign(draft, issue);
      }
    }),
  );

  for (const args of argsList) {
    dispatch(
      openapi.util.updateQueryData("getProjectByProjectIdIssuesOnBoard", args, (draft: IIssueApi[]) => {
        const idx = draft.findIndex((i) => sameId(i._id, issue._id));
        if (idx >= 0) {
          draft[idx] = issue;
        }
      }),
    );
  }

  patchMultiRegisterIssueCaches(issue, getState, dispatch);

  if (issue.taskOrIssue === "TASK") {
    const connectedIssueId = issue.connectedIssue?._id;

    for (const args of argsList) {
      dispatch(
        openapi.util.updateQueryData("getProjectByProjectIdIssuesOnBoard", args, (draft: IIssueApi[]) => {
          for (const parentIssue of draft) {
            if (!sameId(parentIssue._id, connectedIssueId)) continue;
            const taskIndex = parentIssue.connectedTasks?.findIndex((task) => sameId(task._id, issue._id)) ?? -1;
            if (taskIndex >= 0 && parentIssue.connectedTasks != null) {
              parentIssue.connectedTasks[taskIndex] = issue;
            }
            return;
          }
        }),
      );
    }

    for (const args of getConnectedTasksArgs(getState, trpcSlice)) {
      dispatch(
        openapi.util.updateQueryData("getProjectByProjectIdConnectedTasks", args, (draft: IIssueApi[]) => {
          const taskIndex = draft.findIndex((task) => sameId(task._id, issue._id));
          if (taskIndex >= 0) {
            draft[taskIndex] = issue;
          }
        }),
      );
    }
  }

  const notifications = (getState() as any)?.globalReducer?.user?.notifications;
  if (Array.isArray(notifications) && notifications.some((notification) => sameId(notification.itemDBid, issue._id))) {
    dispatch(
      setNotificationInRedux(
        notifications.map((notification) =>
          sameId(notification.itemDBid, issue._id) ? { ...notification, issueStatus: issue.status } : notification,
        ),
      ),
    );
  }

  dispatch(openapi.util.invalidateTags([{ type: "IssueLogs" }]));
  dispatch(openapi.util.invalidateTags(["TrpcMembersByUserRelation", "Whoami"]));
  dispatch(openapi.util.invalidateTags([{ type: "IssueLogs" }, { type: "FormulaGroupIssues" }]));
};
